import { Dropdown, IDropdownOption } from "@fluentui/react";
import { ReactElement, useEffect, useState } from "react";
import { SmallBody } from "src/components";
import { localize } from "src/l10n";
import { FormControl } from "src/ui/components/Forms";
import { fetchAttentionCount } from "../../api";
import { AppNotificationSetting } from "../../types";
import "./AttentionPushSetting.scss";

interface IProps {
    value: AppNotificationSetting;
    onChange: (value: AppNotificationSetting) => void;
}

/**
 * Whether to be pushed when something in Just nu goes past its last day.
 *
 * Hidden for anyone the surface is not switched on for. A setting for something
 * they cannot see is a question they have no way to answer.
 */
function AttentionPushSetting(props: IProps): ReactElement | null {
    const { value, onChange } = props;

    const [isApplicable, setIsApplicable] = useState<boolean>(false);

    useEffect(() => {
        const controller = new AbortController();

        // The count rather than the list: all this needs to know is whether the
        // surface is this person's at all.
        fetchAttentionCount(controller.signal)
            .then((count) => {
                if (!controller.signal.aborted) {
                    setIsApplicable(count.applicable);
                }
            })
            .catch(() => {
                // Leaving the row out is the safe failure. Showing a control bound
                // to a setting we could not read would invite saving a guess.
            });

        return () => controller.abort();
    }, []);

    if (!isApplicable) {
        return null;
    }

    const options: IDropdownOption[] = [
        { key: AppNotificationSetting.Off, text: localize("Av") },
        { key: AppNotificationSetting.On, text: localize("Pa") },
        { key: AppNotificationSetting.DuringWorkHours, text: localize("ATTENTION_PUSH_DURING_WORK_HOURS") },
    ];

    return (
        <FormControl label={localize("ATTENTION_PUSH_LABEL")}>
            <Dropdown
                className="AttentionPushSetting-dropdown"
                options={options}
                selectedKey={value ?? AppNotificationSetting.On}
                onChange={(_event, option) => onChange(option.key as AppNotificationSetting)}
            />
            <SmallBody color="contentLight">{localize("ATTENTION_PUSH_HELP")}</SmallBody>
        </FormControl>
    );
}

export default AttentionPushSetting;
